import { useCallback, useState } from 'react';

const useAccordion = ({
  initialExpanded = [0],
  multiExpand,
  itemCount,
}: {
  initialExpanded?: number[];
  multiExpand?: boolean;
  itemCount: number;
}) => {
  const getExpandedItems = useCallback(
    () =>
      initialExpanded.length > 1 && !multiExpand
        ? [Math.min(...initialExpanded)]
        : initialExpanded,
    [initialExpanded, multiExpand],
  );

  const [expandedItems, setExpandedItems] = useState<number[]>(
    itemCount > 0 && initialExpanded.length === 0 ? [] : getExpandedItems(),
  );

  const expandItem = useCallback(
    (index: number) => {
      if (!multiExpand) {
        setExpandedItems([index]);
      } else {
        setExpandedItems([index, ...expandedItems]);
      }
    },
    [expandedItems, multiExpand],
  );

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, index: number) => {
      e.preventDefault();
      e.stopPropagation();

      if (expandedItems.includes(index)) {
        setExpandedItems(expandedItems.filter((i) => i !== index));
      } else {
        expandItem(index);
      }
    },
    [expandItem, expandedItems],
  );

  const getItemProps = useCallback(
    ({ index }: { index: number }) => {
      const buttonId = `accordion-item-${index}-button`;
      const containerId = `accordion-item-${index}-container`;

      const buttonProps = {
        id: buttonId,
        'aria-controls': containerId,
      };

      const containerProps = {
        id: containerId,
        /**
         * https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
         * "Avoid using the region role in circumstances that create landmark region proliferation,
         * e.g., in an accordion that contains more than approximately 6 panels that can be expanded at the same time."
         */
        ...((!multiExpand || itemCount <= 6) && {
          role: 'region',
          /**
           * aria-labelledby attribute is not well-supported on a div with no valid role attribute
           * so only add this if we are also adding the role
           */
          'aria-labelledby': buttonId,
        }),
      };

      return {
        expanded: expandedItems.includes(index),
        onExpand: (e: React.MouseEvent<HTMLElement>) => onClick(e, index),
        buttonProps,
        containerProps,
      };
    },
    [expandedItems, itemCount, multiExpand, onClick],
  );

  return { getItemProps, expandedItems, expandItem };
};

export default useAccordion;
